import React, { useState } from 'react';
import { Field, Formik, FormikErrors, useFormikContext } from 'formik';
import { tWithKeyAttr as t } from "../../../i18nHelper";
import Select from 'react-select';
import styles from '../../forms/forms.module.css';
import { IUserReport, TCompanyLabeledOption } from '../../../services/types/users.types';
import { TContactPerson } from '../../../services/types/user.types';
import Modal from '../../modals/modal/Modal';
import ChangePasswordPopup from '../../modals/changePasswordPopup/changePasswordPopup';
import SelectContactPopup from '../../modals/selectContactPopup/selectContactPopup';
import { useLazyGetUsersByCompanyIdWithRecommendsQuery, useAdminResetUserPasswordMutation } from '../../../services/api/user.api';
import ErrorPopup from '../../modals/errorPopup/errorPopup';
import GeneralSuccessPopup from '../../modals/generalSuccessPopup/generalSuccessPopup';
import { useAppSelector } from '../../../services/hooks';
import { adminSelector } from '../../../services/selectors/user.selectors';

interface ContactDetailsSectionProps {
  index: number;
  onRemove: () => void;
  companiesOptions: TCompanyLabeledOption[];
  genders: Array<{ value: number; label: string | number }>;
  onClose: () => void;
  handleChange?: (index: number, field: string, value: any) => void; // optional
  usersRoleId?: number;
  hideHeader?: boolean;
}

function ContactDetailsSection({ index, onRemove, companiesOptions, genders, onClose, handleChange, usersRoleId, hideHeader = false }: ContactDetailsSectionProps) {

  const { setFieldValue, values, errors, validateForm, setFieldTouched } = useFormikContext<{ contacts: TContactPerson[] }>();
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState<string | undefined>(undefined);
  // safe access to contacts errors (Formik errors can be string | object | array)
  const contactsErrors = (errors as any)?.contacts as Array<any> | undefined;
  const [openPasswordModal, setOpenPasswordModal] = useState(false);

  const [adminResetUserPassword] = useAdminResetUserPasswordMutation();
  const [getAllUsersByCompanyIdWithRecommends] =
    useLazyGetUsersByCompanyIdWithRecommendsQuery();

  const admin = useAppSelector(adminSelector);

  const [openSelectContactModal, setOpenSelectContactModal] = useState(false);
  const [contactsList, setContactsList] = useState<IUserReport[]>([]);

  // Check if contact fields are already filled
  const currentContact = values.contacts?.[index];
  const isContactFilled = !!(
    currentContact?.firstName ||
    currentContact?.lastName ||
    currentContact?.phone ||
    currentContact?.email ||
    currentContact?.passport
  );

  const handleChooseExistingUser = () => {
    getAllUsersByCompanyIdWithRecommends({
      companyIds: [admin.companyId],
      roleIds: [usersRoleId == -2 ? 1 : -2],
      fakeCompanyIds: [admin?.fakeCompanyId || -1],
    }).then((res) => {
      if ("data" in res && res.data?.Users) {
        setContactsList(res.data.Users);
        setOpenSelectContactModal(true);
      } else {
        setOpenErrorModal(t('SOMETHING_WENT_WRONG_TRY_AGAIN_LATER'));
      }
    });
  };

  const handleSelectContact = (contact: IUserReport) => {
    const user = contact.Users;

    setFieldValue(`contacts.${index}.firstName`, user.FirstName || '');
    setFieldValue(`contacts.${index}.lastName`, user.LastName || '');
    setFieldValue(`contacts.${index}.phone`, user.Phone || '');
    setFieldValue(`contacts.${index}.email`, user.Email || '');
    setFieldValue(`contacts.${index}.passport`, user.Passport || '');
    setFieldValue(`contacts.${index}.genderId`, user.GenderId);
    setFieldValue(`contacts.${index}.fakeCompanyId`, user.FakeCompanyId);
    setFieldValue(`contacts.${index}.guid`, user.Guid || '');
    setFieldValue(`contacts.${index}.id`, user.Id);
    // Clear password as we're selecting an existing user
    setFieldValue(`contacts.${index}.password`, '');
    // Keep relation empty - user should fill it manually

    // Trigger validation to enable submit button
    setTimeout(() => {
      validateForm();
    }, 100);
  };

  const handleUpdatePassword = async (password: string) => {
    if (values.contacts?.[index]?.guid) {
      try {
        await adminResetUserPassword({
          targetUserGuid: values.contacts[index].guid ?? "",
          newPassword: password,
        }).unwrap();
        setOpenSuccessModal(true);
      } catch {
        setOpenErrorModal(t('SOMETHING_WENT_WRONG_TRY_AGAIN_LATER'));
      }
    } else {
      if (values.contacts && values.contacts[index]) {
        values.contacts[index].password = password;
      }
    }
  };




  return (
    <div className={styles.form__section}>
      {!hideHeader && (
        <div className={styles.form__sectionHeader}>
          <div>
            <h2 className={`title ${styles.form__sectionMainTitle}`}>
              {usersRoleId == 1 ? t('CONTACT_PERSON_DETAILS') : t("PATIENT_CONTACT_DETAILS")}
            </h2>
            {index !== 0 && (
              <h4 className={styles.form__sectionTitle}>{usersRoleId == 1 ? t('CONTACT_PERSON') : t("PATIENT_CONTACT")}{index + 1}</h4>
            )}
          </div>
          <div className={styles.form__sectionHeaderButtons}>
            <button
              type="button"
              onClick={handleChooseExistingUser}
              disabled={isContactFilled}
              className={`${styles.form__smallButton} ${isContactFilled ? styles.form__smallButton_disabled : ''}`}
            >
              {t('CHOOSE_EXISTING_USER')}
            </button>
            <button
              type="button"
              onClick={onRemove}
              className="btn btn_type_delete"
            />
          </div>
        </div>
      )}
      
      {/* Choose Existing User button for tabbed view */}
      {hideHeader && (
        <div className={styles.form__sectionHeaderButtons} style={{ marginBottom: '16px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={handleChooseExistingUser}
            disabled={isContactFilled}
            className={`${styles.form__smallButton} ${isContactFilled ? styles.form__smallButton_disabled : ''}`}
          >
            {t('CHOOSE_EXISTING_USER')}
          </button>
        </div>
      )}

      {openSelectContactModal && (
        <Modal onClose={() => setOpenSelectContactModal(false)} wide>
          <SelectContactPopup
            contacts={contactsList}
            onConfirm={(selected) => {
              if (selected.length > 0) handleSelectContact(selected[0]);
            }}
            onClose={() => setOpenSelectContactModal(false)}
          />
        </Modal>
      )}

      <ul className={`${styles.form__list} ${styles.form__list_type_fourColumns}`}>
        {/* First Name */}
        <li>
          <label className={styles.form__label}>{t('FIRST_NAME')}</label>
          <Field
            name={`contacts.${index}.firstName`}
            type="text"
            value={values.contacts?.[index]?.firstName || ''}
            className={`${styles.form__input} ${contactsErrors?.[index]?.firstName ? styles.form__input_invalid : ''
              }`}
            placeholder={t('FIRST_NAME')}
          />
        </li>

        {/* Last Name */}
        <li>
          <label className={styles.form__label}>{t('LAST_NAME')}</label>
          <Field
            name={`contacts.${index}.lastName`}
            type="text"
            value={values.contacts?.[index]?.lastName || ''}
            className={styles.form__input}
            placeholder={t('LAST_NAME')}
          />
        </li>

        {/* ID / Passport */}
        <li>
          <label className={styles.form__label}>{t('ID_NUMBER')}</label>
          <Field
            name={`contacts.${index}.passport`}
            type="text"
            value={values.contacts?.[index]?.passport || ''}
            className={styles.form__input}
            placeholder={t('ID_NUMBER')}
          />
        </li>

        {/* Gender */}
        <li>
          <label className={styles.form__label}>{t('GENDER')}</label>
          <Select
            name={`contacts.${index}.genderId`}
            inputId={`contacts.${index}.genderId`}
            options={genders}
            value={genders.find(
              (g) => g.value === values.contacts?.[index]?.genderId
            )}
            placeholder={t('SELECT_PLACEHOLDER')}
            className={styles.select}
            onChange={(value) => {
              if (value) {
                setFieldValue(`contacts.${index}.genderId`, value.value);
              }
            }}
          />
        </li>

        {/* Mobile Phone */}
        <li>
          <label className={styles.form__label}>{t('MOBILE_PHONE')}</label>
          <Field
            name={`contacts.${index}.phone`}
            type="text"
            value={values.contacts?.[index]?.phone || ''}
            className={`${styles.form__input} ${contactsErrors?.[index]?.phone ? styles.form__input_invalid : ''
              }`} y
            placeholder={t('MOBILE_PHONE')}
          />
        </li>

        {/* Contact Password (opens ChangePasswordPopup) */}
        <li>
          <label className={styles.form__label}>{t('PASSWORD')}</label>
          <input
            type="password"
            name={`contacts.${index}.password`}
            value={values.contacts?.[index]?.password || ''}
            readOnly
            onClick={() => setOpenPasswordModal(true)}
            className={`${styles.form__input} ${contactsErrors?.[index]?.password ? styles.form__input_invalid : ''}`}
            placeholder={t('PASSWORD')}
          />
          {openSuccessModal && (
            <Modal
              onClose={() => {
                setOpenSuccessModal(false);
                onClose();
              }}
            >
              <GeneralSuccessPopup
                onClose={() => {
                  console.log("GeneralSuccessPopup onClose called");
                  setOpenSuccessModal(false);
                  console.log("Calling onClose from addNewUserForm");
                  onClose();
                }}
              />
            </Modal>
          )}
          {openErrorModal && (
            <Modal
              onClose={() => {
                setOpenErrorModal(undefined);
              }}
            >
              <ErrorPopup
                text={openErrorModal}
                onClose={() => {
                  setOpenErrorModal(undefined);
                }}
              />
            </Modal>
          )}
          {openPasswordModal && (
            <Modal onClose={() => setOpenPasswordModal(false)}>
              <ChangePasswordPopup
                onClose={() => setOpenPasswordModal(false)}
                updatePassword={(password: string) => handleUpdatePassword(password)}
              />
            </Modal>
          )}
        </li>

        {/* Email */}
        <li>
          <label className={styles.form__label}>{t('EMAIL')}</label>
          <Field
              name={`contacts.${index}.email`}
            type="email"
            value={values.contacts?.[index]?.email || ''}
            className={`${styles.form__input} ${contactsErrors?.[index]?.email ? styles.form__input_invalid : ''}`}
            placeholder={t('EMAIL')}
          />
        </li>

        {/* Subsidiary */}
        <li>
          <label className={styles.form__label}>{t('SUBSIDIARY')}</label>
          <Select
            name={`contacts.${index}.fakeCompanyId`}
            inputId={`contacts.${index}.fakeCompanyId`}
            options={companiesOptions}
            value={companiesOptions.find(
              (c) => c.value === values.contacts?.[index]?.fakeCompanyId
            ) || undefined}
            placeholder={t('PLEASE_SELECT_SUBSIDIARY')}
            className={styles.select}
            onChange={(value) => {
              if (value) {
                setFieldValue(
                  `contacts.${index}.fakeCompanyId`,
                  value.Id === -2 ? null : value.Id
                );
              }
            }}
          />
        </li>

        {/* Relation to Patient */}
        <li>
          <label className={styles.form__label}>{t('RELATION_TO_PATIENT')}</label>
          <Field
            name={`contacts.${index}.relation`}
            type="text"
            value={values.contacts?.[index]?.relation || ''}
            className={styles.form__input}
            placeholder={t('RELATION_TO_PATIENT')}
          />
        </li>
      </ul>
    </div>
  );
}

export default ContactDetailsSection;
